import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import NavBar from "@/components/NavBar";
import StatusPill from "@/components/StatusPill";
import AgentTrustCard from "@/components/AgentTrustCard";
import api, { formatApiError } from "@/lib/api";
import { taskUrgency, sortByUrgency } from "@/lib/taskUrgency";
import { CLIENT } from "@/constants/testIds";
import { Plus, MapPin, Calendar, Banknote, ArrowRight, ShieldCheck, Sparkles, Camera, AlertTriangle } from "lucide-react";

export default function ClientDashboard() {
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const nav = useNavigate();

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/tasks");
        setTasks(data);
      } catch (e) {
        setErr(formatApiError(e));
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const inFlight = sortByUrgency(tasks.filter((t) => ["open", "assigned", "in_progress", "awaiting_approval"].includes(t.status)));
  const done = sortByUrgency(tasks.filter((t) => ["completed", "cancelled"].includes(t.status)));

  return (
    <div className="min-h-screen">
      <NavBar />
      <main className="max-w-7xl mx-auto px-6 md:px-10 py-10">
        <section className="grid md:grid-cols-12 gap-6 mb-10">
          <div className="md:col-span-8 eb-card p-8 relative overflow-hidden">
            <div className="absolute -top-20 -right-20 w-72 h-72 rounded-full bg-[var(--eb-primary)] opacity-10 blur-3xl" />
            <span className="eb-chip"><Sparkles className="w-3.5 h-3.5" /> Welcome home</span>
            <h1 className="mt-4 text-4xl md:text-5xl">Send a task to Nigeria.</h1>
            <p className="mt-3 text-[var(--eb-text-dim)] max-w-lg">
              Pick a service. Set a budget. Fund escrow. Your verified agent takes it from there — with photo proof at every milestone.
            </p>
            <div className="mt-7 flex flex-wrap gap-3">
              <Link to="/tasks/new" data-testid={CLIENT.createTask} className="eb-btn eb-btn-primary">
                <Plus className="w-4 h-4" /> Create a task
              </Link>
              <button onClick={() => nav("/tasks/new")} className="eb-btn eb-btn-ghost">
                <Camera className="w-4 h-4" /> Book a check-in
              </button>
            </div>
          </div>
          <div className="md:col-span-4 eb-card p-7">
            <div className="text-xs tracking-[0.2em] uppercase text-[var(--eb-text-dim)]">Your trust score</div>
            <div className="mt-3 flex items-baseline gap-2">
              <span className="text-5xl font-[800] text-[var(--eb-primary)]">A+</span>
              <span className="text-[var(--eb-text-dim)] text-sm">Escrow protected</span>
            </div>
            <div className="mt-4 space-y-2 text-sm">
              <div className="flex items-center gap-2"><ShieldCheck className="w-4 h-4 text-[var(--eb-success)]" /> Up to $100k coverage</div>
              <div className="flex items-center gap-2"><ShieldCheck className="w-4 h-4 text-[var(--eb-success)]" /> Identity &amp; In-Person Verified agents</div>
              <div className="flex items-center gap-2"><ShieldCheck className="w-4 h-4 text-[var(--eb-success)]" /> Photo proof or money back</div>
            </div>
          </div>
        </section>

        <section>
          <div className="flex items-end justify-between mb-5">
            <h2 className="text-2xl">Active tasks</h2>
            <span className="text-xs text-[var(--eb-text-dim)] tabular-nums">{inFlight.length} in flight</span>
          </div>
          {loading ? (
            <SkeletonRows />
          ) : inFlight.length === 0 ? (
            <EmptyState onCta={() => nav("/tasks/new")} />
          ) : (
            <div className="grid md:grid-cols-2 gap-5">
              {inFlight.map((t) => <TaskCard key={t.task_id} t={t} />)}
            </div>
          )}
        </section>

        {done.length > 0 && (
          <section className="mt-12">
            <h2 className="text-2xl mb-5">History</h2>
            <div className="grid md:grid-cols-2 gap-5">
              {done.map((t) => <TaskCard key={t.task_id} t={t} muted />)}
            </div>
          </section>
        )}

        {err && <div className="mt-6 text-[var(--eb-secondary)] text-sm">{err}</div>}
      </main>
    </div>
  );
}

function TaskCard({ t, muted }) {
  const u = taskUrgency(t);
  return (
    <Link
      to={`/tasks/${t.task_id}`}
      data-testid={CLIENT.taskCard}
      data-urgency={u.bucket}
      className={`eb-card p-6 block hover:border-[var(--eb-primary)] transition-colors relative ${u.ringClass} ${u.bgClass} ${muted && u.bucket === "normal" ? "opacity-80" : ""}`}
    >
      <span className={`absolute top-3 right-3 w-2 h-2 rounded-full ${u.dotClass}`} aria-hidden="true" />
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="text-[10px] tracking-[0.2em] uppercase text-[var(--eb-text-dim)]">{t.category_label}</div>
          <div className="mt-1 text-xl font-[700] line-clamp-1">{t.title}</div>
        </div>
        <StatusPill status={t.status} />
      </div>
      <p className="mt-3 text-sm text-[var(--eb-text-dim)] line-clamp-2">{t.description}</p>
      <div className="mt-5 flex flex-wrap items-center gap-x-5 gap-y-2 text-xs text-[var(--eb-text-dim)]">
        <span className="inline-flex items-center gap-1"><MapPin className="w-3.5 h-3.5" /> {t.city}</span>
        {t.budget_ngn ? <span className="inline-flex items-center gap-1"><Banknote className="w-3.5 h-3.5" /> ₦{t.budget_ngn.toLocaleString()}</span> : null}
        {t.deadline ? <span className="inline-flex items-center gap-1"><Calendar className="w-3.5 h-3.5" /> {new Date(t.deadline).toLocaleDateString()}</span> : null}
        {u.label && (
          <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] uppercase tracking-[0.15em] border ${u.badgeClass}`}>
            {u.bucket === "late" && <AlertTriangle className="w-3 h-3" />}
            {u.label}
          </span>
        )}
        <span className="ml-auto inline-flex items-center gap-1 text-[var(--eb-primary)]">Open <ArrowRight className="w-3.5 h-3.5" /></span>
      </div>
      <EscrowPills t={t} />
      {t.agent_id && (
        <div className="mt-4 pt-3 border-t border-dashed border-[var(--eb-border)]">
          <AgentTrustCard agentId={t.agent_id} agentName={t.agent_name} variant="inline" />
        </div>
      )}
    </Link>
  );
}

function EscrowPills({ t }) {
  return (
    <div className="mt-3 flex gap-2 text-[10px] tracking-[0.18em] uppercase">
      <span className={`px-2 py-0.5 rounded-full border ${t.service_fee_status === "held_escrow" || t.service_fee_status === "released" ? "border-[var(--eb-success)] text-[var(--eb-success)]" : "border-[var(--eb-border)] text-[var(--eb-text-faint)]"}`}>
        Service fee: {t.service_fee_status?.replace("_", " ")}
      </span>
      {t.budget_ngn > 0 && (
        <span className={`px-2 py-0.5 rounded-full border ${t.item_cost_status === "held_escrow" || t.item_cost_status === "released" ? "border-[var(--eb-success)] text-[var(--eb-success)]" : "border-[var(--eb-border)] text-[var(--eb-text-faint)]"}`}>
          Item: {t.item_cost_status?.replace("_", " ")}
        </span>
      )}
    </div>
  );
}

function EmptyState({ onCta }) {
  return (
    <div data-testid={CLIENT.emptyState} className="eb-card p-10 text-center">
      <div className="mx-auto w-14 h-14 rounded-2xl bg-[var(--eb-surface-2)] flex items-center justify-center">
        <Plus className="w-6 h-6 text-[var(--eb-primary)]" />
      </div>
      <h3 className="mt-5 text-2xl">No tasks yet — let&apos;s change that.</h3>
      <p className="mt-2 text-[var(--eb-text-dim)] max-w-md mx-auto">
        Send a parent check-in, a document pickup, or a birthday gift. Your first task can be live in under an hour.
      </p>
      <button onClick={onCta} className="eb-btn eb-btn-primary mt-6">
        <Plus className="w-4 h-4" /> Create your first task
      </button>
    </div>
  );
}

function SkeletonRows() {
  return (
    <div className="grid md:grid-cols-2 gap-5">
      {[1, 2].map((i) => (
        <div key={i} className="eb-card p-6 animate-pulse">
          <div className="h-3 w-24 bg-[var(--eb-border)] rounded" />
          <div className="mt-3 h-5 w-2/3 bg-[var(--eb-border)] rounded" />
          <div className="mt-2 h-3 w-full bg-[var(--eb-border)] rounded" />
        </div>
      ))}
    </div>
  );
}
